




































import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  PropType,
} from '@vue/composition-api';
import { Toast, Loading } from '@hb/coffee';
import { queryMiddleIncomeCreditResult } from '@/api/middle-income';
import { openThirdPage } from '@/api/native/utils';
import {
  CreditMallFromEnum, AgreementStyleEnum, PageStatusEnum, BusinessTypeEnum,
} from '@/api/middle-income/types';
import type { MiddleIncomeCreditResultParams, MiddleIncomeCreditResultResponse } from '@/api/middle-income/types';

import {
  AbExperimentKey,
  getAbResultByKey,
  isSkipCountdownGroup,
} from '@/shared/ab-experiment/shopMallExperiments';
import { ubtModuleExposeNew, ubtPageButtonNew } from '@/shared/utils/ubt';
import router from '@/router';
import { appVersion } from '@/shared/utils/platform';
import { pollingStatus } from './pollingStatus';
import {
  shopMallModuleClick,
  shopMallModuleExpose,
  dcHalfApplyCard,
} from './tianqi';

export default defineComponent({
  name: 'InstallmentMall',
  components: {
    Loading,
  },
  props: {
    label: {
      type: String,
      default: '最高可享',
    },
    unit: {
      type: String,
      default: '元',
    },
    buttonText: {
      type: String,
      default: '立即申请',
    },
    agreementPrefix: {
      type: String,
      default: '阅读并同意',
    },
    showMoreProduct: {
      type: Boolean,
      default: false,
    },
    themeType: {
      type: String,
      default: 'styleA',
    },
    /** 来源标识 */
    from: {
      type: String as PropType<CreditMallFromEnum>,
      default: CreditMallFromEnum.FINANCE_WITHDRAW_AGREEMENT,
    },
    /** 授信任务ID（有值则透传给结果查询接口） */
    taskId: {
      type: String,
      default: '',
    },
  },
  emits: ['apply', 'agreement-click'],
  setup(props, { emit }) {
    const showCard = ref(false);
    const cardAmount = ref<string | number>('200,000');
    const creditStatus = ref<PageStatusEnum | undefined>(undefined);
    /** 缓存概要信息 */
    const summaryData = ref<MiddleIncomeCreditResultResponse>();
    /** 倒计时秒数 */
    const countdown = ref(0);
    /** 倒计时定时器 */
    let countdownTimer: ReturnType<typeof setInterval> | null = null;
    /** AB 与轮询同时执行，授信成功时 await 此 Promise 再决定直跳或倒计时 */
    let abResultPromise: Promise<boolean> = Promise.resolve(false);

    const isDisabled = computed(() => (
      creditStatus.value === PageStatusEnum.PROCESSING
    ));

    const formatAmount = computed(() => {
      if (typeof cardAmount.value === 'number'
        || (typeof cardAmount.value === 'string' && Number(cardAmount.value))) {
        return Number(cardAmount.value).toLocaleString();
      }
      return cardAmount.value;
    });

    /**
     * 获取按钮文案
     */
    const getButtonText = (status: PageStatusEnum | undefined): string => {
      if (!status) {
        return props.buttonText;
      }
      switch (status) {
        case PageStatusEnum.INIT:
          return '立即申请';
        case PageStatusEnum.FAILED:
          return '额度申请未通过';
        case PageStatusEnum.PROCESSING:
          return '授信中';
        case PageStatusEnum.SUCCEEDED:
          return countdown.value > 0 ? `去使用(${countdown.value}s)` : '去使用';
        default:
          return props.buttonText;
      }
    };

    const buttonTextComputed = computed(() => getButtonText(creditStatus.value));
    const cardTitle = computed(() => {
      if (creditStatus.value === PageStatusEnum.SUCCEEDED) {
        return '您已获得额度(元)';
      }
      return '最高可获得额度(元)';
    });

    /**
     * 初始化数据
     */
    const initData = async (): Promise<MiddleIncomeCreditResultResponse | undefined> => {
      try {
        const params: MiddleIncomeCreditResultParams = { from: props.from, version: appVersion || '5.13.0' };
        if (props.taskId) {
          params.taskId = props.taskId;
        }
        const response = await queryMiddleIncomeCreditResult(params);
        /** 有结果且status不是undefined 且 status不是FAILED，则认为是有效结果 */
        if (response
          && (response.status !== undefined && response.status !== PageStatusEnum.FAILED)) {
          summaryData.value = response;
          if (response.creditAmount) {
            cardAmount.value = response.creditAmount;
          }
          creditStatus.value = response.status;
          showCard.value = true;
          if (response.businessType === BusinessTypeEnum.SHOPPING_MALL) {
            ubtModuleExposeNew(router.currentRoute.meta?.pageId as string, shopMallModuleExpose, {
              from: props.from,
            });
          } else if (response.businessType === BusinessTypeEnum.OVER_LOAN) {
            ubtModuleExposeNew(router.currentRoute.meta?.pageId as string, dcHalfApplyCard,
              {
                from: props.from,
              });
          }
          return response;
        }
        showCard.value = false;
        return undefined;
      } catch (error) {
        showCard.value = false;
        return undefined;
      }
    };

    /**
     * 轮询授信状态
     */
    const polling = pollingStatus({
      from: props.from,
      taskId: () => (props.taskId ? props.taskId : undefined),
      onStatusUpdate: (status) => {
        creditStatus.value = status;
      },
      onSuccess: async (res) => {
        /** 成功以后，summaryData.value 和 cardAmount.value 需要更新 */
        summaryData.value = res;
        if (res.creditAmount) {
          cardAmount.value = res.creditAmount;
        }
        const jumpUrl = res?.jumpUrl || summaryData.value?.jumpUrl;
        if (!jumpUrl) {
          Toast('未能获取到跳转地址，请稍后再试');
          return;
        }

        // 清除之前的定时器
        if (countdownTimer) {
          clearInterval(countdownTimer);
          countdownTimer = null;
        }

        // 授信成功时再取 AB 结果（与轮询并行请求，此处 await 保证值正确）
        const skipCountdown = await abResultPromise;
        if (skipCountdown) {
          countdown.value = 0;
          openThirdPage(jumpUrl);
          return;
        }

        // 对照组：3s 倒计时后自动跳转
        Toast('授信成功！3s后自动跳转');
        countdown.value = 3;
        countdownTimer = setInterval(() => {
          countdown.value -= 1;
          if (countdown.value <= 0) {
            if (countdownTimer) {
              clearInterval(countdownTimer);
              countdownTimer = null;
            }
            countdown.value = 0;
            openThirdPage(jumpUrl);
          }
        }, 1000);
      },
      onFailed: () => {
        showCard.value = false;
        Toast('本次额度审核失败，保持良好信用下次再试');
      },
      onTimeout: () => {
        // creditStatus.value = PageStatusEnum.FAILED;
        // Toast('未查询到开通结果');
      },
    });
    /**
     * 处理成功跳转
     */
    const handleSuccess = () => {
      if (creditStatus.value === PageStatusEnum.SUCCEEDED) {
        // 清除倒计时定时器
        if (countdownTimer) {
          clearInterval(countdownTimer);
          countdownTimer = null;
        }
        countdown.value = 0;
        if (!summaryData.value?.jumpUrl) {
          Toast('未能获取到跳转地址，请稍后再试');
          return;
        }
        openThirdPage(summaryData.value?.jumpUrl || '');
      }
    };

    /**
     * 获取分期商城概要信息
     */
    const fetchInstallmentMallData = async ():
      Promise<MiddleIncomeCreditResultResponse | undefined> => {
      try {
        const res = await initData();
        if (res?.status === PageStatusEnum.PROCESSING) {
          abResultPromise = getAbResultByKey(
            AbExperimentKey.APPLY_RESULT_MALL_SUCCESS_COUNTDOWN,
          ).then((r) => isSkipCountdownGroup(r));
          polling.start();
        }
        return res;
      } catch (error) {
        showCard.value = false;
        return undefined;
      }
    };

    /**
     * 处理申请按钮点击
     */
    const handleApply = async () => {
      if (summaryData.value?.businessType === BusinessTypeEnum.SHOPPING_MALL) {
        ubtPageButtonNew(router.currentRoute.meta?.pageId as string, shopMallModuleClick, {
          from: props.from,
        });
      } else if (summaryData.value?.businessType === BusinessTypeEnum.OVER_LOAN) {
        ubtPageButtonNew(router.currentRoute.meta?.pageId as string, dcHalfApplyCard, {
          from: props.from,
        });
      }
      if (creditStatus.value === PageStatusEnum.SUCCEEDED) {
        handleSuccess();
        return;
      }
      if (creditStatus.value === PageStatusEnum.INIT) {
        /** 方案二样式，按钮立即申请 回调 */
        if (props.themeType === AgreementStyleEnum.SCHEME_B) {
          emit('apply');
        }
      }
    };

    onMounted(async () => {
      await fetchInstallmentMallData();
    });

    onBeforeUnmount(() => {
      polling.cancel();
      // 清除倒计时定时器
      if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
    });

    return {
      showCard,
      cardTitle,
      cardAmount,
      creditStatus,
      summaryData,
      isDisabled,
      formatAmount,
      buttonTextComputed,
      handleApply,
      PageStatusEnum,
    };
  },
});
